package com.sddamico.draftmachine.ds.forms

import androidx.compose.runtime.Composable
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmSpacing
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.PressableStyle
import com.sddamico.draftmachine.ds.boxShadowRaw
import com.sddamico.draftmachine.ds.displayInlineFlex
import com.sddamico.draftmachine.ds.spin
import com.sddamico.draftmachine.ds.textureBrushed
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button as DomButton

/** Copper (primary), brushed steel (secondary), transparent (ghost), rust (danger). */
enum class ButtonVariant { Primary, Secondary, Ghost, Danger }

enum class ButtonSize { Sm, Md, Lg }

/**
 * The primary action control — a stamped brass lever for primary intents, brushed steel for
 * secondary, rust for destructive. Port of `components/forms/Button.jsx`.
 *
 * @param loading swaps the leading glyph for a spinning gear and disables the control.
 * @param icon / iconRight bare Phosphor glyph names.
 */
@Composable
fun Button(
  label: String,
  modifier: Modifier = Modifier,
  variant: ButtonVariant = ButtonVariant.Primary,
  size: ButtonSize = ButtonSize.Md,
  icon: String? = null,
  iconRight: String? = null,
  loading: Boolean = false,
  disabled: Boolean = false,
  fullWidth: Boolean = false,
  onClick: (() -> Unit)? = null,
) {
  val isDisabled = disabled || loading
  val height = when (size) {
    ButtonSize.Sm -> DmSpacing.controlSm
    ButtonSize.Md -> DmSpacing.controlMd
    ButtonSize.Lg -> DmSpacing.controlLg
  }
  val padX = when (size) {
    ButtonSize.Sm -> DmSpacing.space3
    ButtonSize.Md -> DmSpacing.space5
    ButtonSize.Lg -> DmSpacing.space8
  }
  val fontSize = when (size) {
    ButtonSize.Sm -> DmType.textSm
    ButtonSize.Md -> DmType.textBase
    ButtonSize.Lg -> DmType.textMd
  }
  val iconSize = when (size) {
    ButtonSize.Sm -> 16.px
    ButtonSize.Md -> 18.px
    ButtonSize.Lg -> 20.px
  }

  val clickHandler = onClick
  val mod = PressableStyle.toModifier()
    .displayInlineFlex()
    .alignItems(AlignItems.Center)
    .justifyContent(JustifyContent.Center)
    .gap(DmSpacing.space2)
    .height(height)
    .padding(topBottom = 0.px, leftRight = padX)
    .then(if (fullWidth) Modifier.fillMaxWidth() else Modifier)
    .fontFamily(*DmType.body)
    .fontSize(fontSize)
    .fontWeight(DmType.weightBold)
    .letterSpacing(0.01.em)
    .borderRadius(DmEffects.radiusMd)
    .whiteSpace(WhiteSpace.NoWrap)
    .cursor(if (isDisabled) Cursor.NotAllowed else Cursor.Pointer)
    .opacity(if (isDisabled) 0.5 else 1.0)
    .buttonVariant(variant)
    .then(modifier)

  DomButton(
    attrs = mod.toAttrs {
      if (isDisabled) attr("disabled", "")
      if (clickHandler != null) onClick { clickHandler() }
    }
  ) {
    if (loading) {
      Icon("gear-six", IconWeight.Bold, Modifier.fontSize(iconSize).spin("1s"))
    } else if (icon != null) {
      Icon(icon, IconWeight.Bold, Modifier.fontSize(iconSize))
    }
    SpanText(label)
    if (!loading && iconRight != null) {
      Icon(iconRight, IconWeight.Bold, Modifier.fontSize(iconSize))
    }
  }
}

/** The stamped surface for each button variant (background, texture, text, border, top-highlight). */
internal fun Modifier.buttonVariant(variant: ButtonVariant): Modifier = when (variant) {
  ButtonVariant.Primary -> this
    .backgroundColor(DmColor.brass500)
    .textureBrushed()
    .color(DmColor.onPrimary)
    .border(1.px, LineStyle.Solid, DmColor.brass600)
    .boxShadowRaw("inset 0 1px 0 rgba(255,255,255,.28), ${DmEffects.shadowFlat}")

  ButtonVariant.Secondary -> this
    .backgroundColor(DmColor.surfaceRaised)
    .color(DmColor.text)
    .border(1.px, LineStyle.Solid, DmColor.borderStrong)
    .boxShadowRaw("inset 0 1px 0 rgba(232,240,247,.06), ${DmEffects.shadowFlat}")

  ButtonVariant.Ghost -> this
    .backgroundColor(Colors.Transparent)
    .color(DmColor.textSecondary)
    .border(1.px, LineStyle.Solid, Colors.Transparent)

  ButtonVariant.Danger -> this
    .backgroundColor(DmColor.rust500)
    .textureBrushed()
    .color(DmColor.steel50)
    .border(1.px, LineStyle.Solid, DmColor.rust600)
    .boxShadowRaw("inset 0 1px 0 rgba(255,255,255,.20), ${DmEffects.shadowFlat}")
}
